/**
 * Whether analytics may run, and for which site (GIFTWEB-33).
 *
 * Kept as a pure function of its inputs so the decision can be tested without
 * a bundler, a browser or a live tracker. `initAnalytics` supplies the real
 * environment; everything here is arithmetic on strings.
 */

export type AnalyticsEnv = {
  /** `VITE_PLAUSIBLE_DOMAIN` — the Plausible site this build reports to. */
  domain?: string;
  /** Where the page is actually being served from. */
  hostname?: string;
};

export type AnalyticsTarget =
  | { enabled: true; domain: string }
  | { enabled: false; reason: string };

/** Hostnames that are somebody's laptop, not a deployment. */
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]', '::1', '']);

const isLocal = (hostname: string) =>
  LOCAL_HOSTNAMES.has(hostname) || hostname.endsWith('.local');

export function resolveAnalyticsTarget(env: AnalyticsEnv): AnalyticsTarget {
  const domain = (env.domain ?? '').trim().toLowerCase();
  const hostname = (env.hostname ?? '').trim().toLowerCase();

  // Off unless switched on. There is no fallback to the production domain:
  // a build that forgot the variable must send nothing, not send everything
  // to the live site.
  if (!domain) {
    return { enabled: false, reason: 'no VITE_PLAUSIBLE_DOMAIN configured' };
  }

  if (isLocal(hostname)) {
    return { enabled: false, reason: `running locally (${hostname || 'no host'})` };
  }

  /**
   * The guard that matters.
   *
   * There is no CI here — builds are made by hand and uploaded to one of two
   * buckets. A production build dropped into the dev bucket would otherwise
   * report real-looking traffic against the live site, and nothing in the page
   * would say so. Rather than trust the uploader, refuse when the site this
   * build reports to is not the site it is being served from.
   */
  if (hostname !== domain) {
    return {
      enabled: false,
      reason: `built for ${domain} but served from ${hostname}`,
    };
  }

  return { enabled: true, domain };
}
